import {LabelPicker, type LabelPickerLabel} from '@github-ui/item-picker-label-tsq'
import type {MilestonePickerMilestone} from '@github-ui/item-picker-milestone'
import {MilestonePicker} from '@github-ui/item-picker-milestone-tsq'
import type {ProjectPickerBaseProject} from '@github-ui/item-picker-project'
import {ProjectPicker} from '@github-ui/item-picker-project-tsq'
import {
  getTokensByType,
  replaceAllFiltersByTypeInSearchQuery,
  replaceTokensDifferentially,
} from '@github-ui/filter-query'
import {useCallback, useMemo, useState} from 'react'

import {createQuickFilterAnchor, type QuickFilterProps} from './quick-filter-shared'

const labelAnchor = createQuickFilterAnchor('Label')
const projectAnchor = createQuickFilterAnchor('Projects')
const milestoneAnchor = createQuickFilterAnchor('Milestones')

export function LabelQuickFilter({owner, query, repo, onQueryChange}: QuickFilterProps) {
  const labelNames = useMemo(() => getTokensByType(query, 'label'), [query])
  const onSelectionChange = useCallback(
    (labels: LabelPickerLabel[]) => {
      onQueryChange(
        replaceTokensDifferentially(
          query,
          labels.map(label => label.name),
          'label',
        ),
      )
    },
    [onQueryChange, query],
  )

  return (
    <LabelPicker
      anchorElement={labelAnchor}
      title="Filter by labels"
      showEditLabelsButton={false}
      repo={repo}
      owner={owner}
      readonly={false}
      onSelectionChange={onSelectionChange}
      labels={[]}
      labelNames={labelNames.length > 0 ? labelNames : undefined}
      canCreateLabel={false}
      enableSuggestedLabels
    />
  )
}

export function ProjectQuickFilter({owner, query, repo, onQueryChange}: QuickFilterProps) {
  const projectTokens = useMemo(() => getTokensByType(query, 'project'), [query])
  const onSelectionChange = useCallback(
    (projects: ProjectPickerBaseProject[]) => {
      const projectValues = projects.map(project => `${owner}/${project.number}`)
      const nextQuery = replaceTokensDifferentially(query, projectValues, 'project')
      if (nextQuery !== query) onQueryChange(nextQuery)
    },
    [onQueryChange, owner, query],
  )
  const getSelectedProjects = useCallback(
    (projects: ProjectPickerBaseProject[]) =>
      projects.filter(project =>
        projectTokens.some(token => project.url.endsWith(`/${token.replace('/', '/projects/')}`)),
      ),
    [projectTokens],
  )

  return (
    <ProjectPicker
      pickerId="repo-pulls-project-filter"
      anchorElement={projectAnchor}
      repo={repo}
      owner={owner}
      onSave={onSelectionChange}
      selectedProjects={[]}
      readonly={false}
      getSelectedProjects={getSelectedProjects}
      title="Filter by projects"
    />
  )
}

export function MilestoneQuickFilter({owner, query, repo, onQueryChange}: QuickFilterProps) {
  const milestoneTokens = useMemo(() => getTokensByType(query, 'milestone'), [query])
  const [activeMilestone, setActiveMilestone] = useState<MilestonePickerMilestone | null>(null)
  const currentMilestone = activeMilestone && milestoneTokens.includes(activeMilestone.title) ? activeMilestone : null
  const onSelectionChange = useCallback(
    (milestones: MilestonePickerMilestone[]) => {
      setActiveMilestone(milestones[0] ?? null)
      onQueryChange(
        replaceAllFiltersByTypeInSearchQuery(
          query,
          milestones.map(milestone => milestone.title),
          'milestone',
        ),
      )
    },
    [onQueryChange, query],
  )

  return (
    <MilestonePicker
      repo={repo}
      owner={owner}
      activeMilestone={currentMilestone}
      anchorElement={milestoneAnchor}
      onSelectionChange={onSelectionChange}
      title="Filter by milestones"
      canCreateMilestone={false}
    />
  )
}
